import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';
import {
  Save,
  Settings,
  Percent,
  AttachMoney,
  Casino,
  Refresh,
  ExpandMore,
  ExpandLess,
  Security,
  Tune,
  Bolt,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { fetchSettings, updateSettings, resetSettings } from '../store/slices/settingsSlice';
import { SettingsForm } from '../types';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

const schema = yup.object().shape({
  inviterCommissionPercent: yup.number()
    .min(0, 'Commission must be at least 0%')
    .max(100, 'Commission cannot exceed 100%')
    .required('Inviter commission is required'),
  serviceFeePercent: yup.number()
    .min(0, 'Service fee must be at least 0%')
    .max(100, 'Service fee cannot exceed 100%')
    .required('Service fee is required'),
  tournamentServiceFeePercent: yup.number()
    .min(0, 'Tournament service fee must be at least 0%')
    .max(100, 'Tournament service fee cannot exceed 100%')
    .required('Tournament service fee is required'),
  minimumCommissionAmount: yup.number()
    .min(0, 'Minimum commission amount must be at least 0')
    .required('Minimum commission amount is required'),
  bonusRegistrationAmount: yup.number()
    .min(0, 'Bonus registration amount must be at least 0')
    .required('Bonus registration amount is required'),
  agentDepositCommissionPercent: yup.number().min(0).max(100).required(),
  agentMinimumClaimAmount: yup.number().min(0).required(),
  depositSelfBonusEnabled: yup.boolean().required(),
  depositSelfBonusAmount: yup.number().min(0).required(),
  depositSelfBonusPercent: yup.number().min(0).max(100).required(),
  depositSelfBonusFirstOnly: yup.boolean().required(),
  depositInviterBonusEnabled: yup.boolean().required(),
  depositInviterBonusAmount: yup.number().min(0).required(),
  depositInviterBonusPercent: yup.number().min(0).max(100).required(),
  depositInviterBonusFirstOnly: yup.boolean().required(),
  drawIntervalSeconds: yup.number()
    .min(1, 'Draw interval must be at least 1 second')
    .max(300, 'Draw interval cannot exceed 300 seconds (5 minutes)')
    .required('Draw interval is required'),
  countdownSeconds: yup.number()
    .min(5, 'Countdown must be at least 5 seconds')
    .max(60, 'Countdown cannot exceed 60 seconds (1 minute)')
    .required('Countdown is required'),
  minimumPlayers: yup.number()
    .min(2, 'Minimum players must be at least 2')
    .max(50, 'Minimum players cannot exceed 50')
    .required('Minimum players is required'),
  isActive: yup.boolean().required('Active status is required'),
  botEnabled: yup.boolean().required('Bot status is required'),
  maxBotsPerRoom: yup.number()
    .min(0, 'Max bots must be at least 0')
    .max(500, 'Max bots cannot exceed 500')
    .required('Max bots per room is required'),
  houseAccountPhone: yup.string().required('House account phone is required'),
  spinHouseLimitDaily: yup.number().min(0).required(),
  bonusSystemEnabled: yup.boolean().required(),
  dailyActiveUserBonusAmount: yup.number().min(0).required(),
  dailyActiveUserMinimumGames: yup.number().min(1).required(),
  topWinnerBonusAmount: yup.number().min(0).required(),
  topWinnerBonusCount: yup.number().min(1).required(),
  topWinnerBonusPeriod: yup.string().oneOf(['daily', 'weekly', 'monthly']).required(),
  topWinnerBonusMinimumWinnings: yup.number().min(0).required(),
  promoCarouselEnabled: yup.boolean().required(),
  midnightLuckyEnabled: yup.boolean().required(),
  midnightLuckyWinnerCount: yup.number().min(1).max(50).required(),
  midnightLuckyAmount: yup.number().min(0).required(),
  midnightLuckyMinGames: yup.number().min(1).required(),
  drawMode: yup.string().oneOf(['fair', 'bot_advantage', 'block_human']).required(),
  flashDepositPromoEnabled: yup.boolean().required(),
  flashDepositMinHoursBetween: yup.number().min(1).required(),
  flashDepositDefaultDurationMinutes: yup.number().min(5).max(180).required(),
});

const SettingsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { settings, loading, error } = useSelector((state: RootState) => state.settings);
  const [saving, setSaving] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    commission: true,
    depositBonus: true,
    serviceFee: true,
    gameSettings: true,
    opsBonus: true,
    systemStatus: true,
  });

  const [flashStatus, setFlashStatus] = useState<{
    active: boolean;
    percent?: number;
    remainingSeconds?: number;
    durationMinutes?: number;
    totalDepositsCredited?: number;
    totalBonusGiven?: number;
  } | null>(null);
  const [flashActionLoading, setFlashActionLoading] = useState(false);
  const [flashActionMsg, setFlashActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchFlashStatus = async () => {
    try {
      const res = await apiService.getFlashDepositStatus();
      setFlashStatus(res);
    } catch (_) {}
  };

  useEffect(() => {
    fetchFlashStatus();
    const timer = setInterval(fetchFlashStatus, 15000);
    return () => clearInterval(timer);
  }, []);

  const handleTriggerFlashPromo = async (percent: number, durationMinutes: number) => {
    setFlashActionLoading(true);
    setFlashActionMsg(null);
    try {
      const res = await apiService.startFlashDepositPromo(percent, durationMinutes);
      if (res.success) {
        setFlashActionMsg({ type: 'success', text: `Successfully launched ${percent}% Flash Bonus for ${durationMinutes} minutes!` });
        await fetchFlashStatus();
      } else {
        setFlashActionMsg({ type: 'error', text: res.message || 'Failed to start promo' });
      }
    } catch (e: any) {
      setFlashActionMsg({ type: 'error', text: e?.message || 'Error triggering promo' });
    } finally {
      setFlashActionLoading(false);
    }
  };

  const handleCancelFlashPromo = async () => {
    setFlashActionLoading(true);
    setFlashActionMsg(null);
    try {
      const res = await apiService.cancelFlashDepositPromo();
      setFlashActionMsg({ type: 'success', text: 'Active Flash Deposit Promo cancelled' });
      await fetchFlashStatus();
    } catch (e: any) {
      setFlashActionMsg({ type: 'error', text: e?.message || 'Error cancelling promo' });
    } finally {
      setFlashActionLoading(false);
    }
  };

  const { control, handleSubmit, reset, watch, formState: { errors, isDirty } } = useForm<SettingsForm>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      inviterCommissionPercent: 5,
      serviceFeePercent: 20,
      tournamentServiceFeePercent: 20,
      minimumCommissionAmount: 1,
      bonusRegistrationAmount: 0,
      agentDepositCommissionPercent: 1,
      agentMinimumClaimAmount: 1000,
      depositSelfBonusEnabled: false,
      depositSelfBonusAmount: 0,
      depositSelfBonusPercent: 0,
      depositSelfBonusFirstOnly: true,
      depositInviterBonusEnabled: false,
      depositInviterBonusAmount: 0,
      depositInviterBonusPercent: 0,
      depositInviterBonusFirstOnly: true,
      drawIntervalSeconds: 30,
      countdownSeconds: 10,
      minimumPlayers: 2,
      isActive: true,
      botEnabled: false,
      maxBotsPerRoom: 300,
      houseAccountPhone: '',
      spinHouseLimitDaily: 0,
      bonusSystemEnabled: false,
      dailyActiveUserBonusAmount: 5,
      dailyActiveUserMinimumGames: 3,
      topWinnerBonusAmount: 50,
      topWinnerBonusCount: 1,
      topWinnerBonusPeriod: 'daily',
      topWinnerBonusMinimumWinnings: 0,
      promoCarouselEnabled: true,
      midnightLuckyEnabled: true,
      midnightLuckyWinnerCount: 5,
      midnightLuckyAmount: 10,
      midnightLuckyMinGames: 1,
      drawMode: 'fair',
      flashDepositPromoEnabled: true,
      flashDepositMinHoursBetween: 6,
      flashDepositDefaultDurationMinutes: 30,
    },
  });

  const systemActive = watch('isActive');
  const adminOnly = watch('botEnabled');

  useEffect(() => {
    dispatch(fetchSettings() as any);
  }, [dispatch]);

  useEffect(() => {
    if (settings) {
      reset({
        inviterCommissionPercent: settings.inviterCommissionPercent,
        serviceFeePercent: settings.serviceFeePercent,
        tournamentServiceFeePercent: settings.tournamentServiceFeePercent,
        minimumCommissionAmount: settings.minimumCommissionAmount,
        bonusRegistrationAmount: settings.bonusRegistrationAmount,
        agentDepositCommissionPercent: settings.agentDepositCommissionPercent ?? 1,
        agentMinimumClaimAmount: settings.agentMinimumClaimAmount ?? 1000,
        depositSelfBonusEnabled: !!settings.depositSelfBonusEnabled,
        depositSelfBonusAmount: settings.depositSelfBonusAmount ?? 0,
        depositSelfBonusPercent: settings.depositSelfBonusPercent ?? 0,
        depositSelfBonusFirstOnly: settings.depositSelfBonusFirstOnly !== false,
        depositInviterBonusEnabled: !!settings.depositInviterBonusEnabled,
        depositInviterBonusAmount: settings.depositInviterBonusAmount ?? 0,
        depositInviterBonusPercent: settings.depositInviterBonusPercent ?? 0,
        depositInviterBonusFirstOnly: settings.depositInviterBonusFirstOnly !== false,
        drawIntervalSeconds: settings.drawIntervalSeconds,
        countdownSeconds: settings.countdownSeconds,
        minimumPlayers: settings.minimumPlayers,
        isActive: settings.isActive,
        botEnabled: settings.botEnabled,
        maxBotsPerRoom: settings.maxBotsPerRoom ?? 300,
        houseAccountPhone: settings.houseAccountPhone || '',
        spinHouseLimitDaily: settings.spinHouseLimitDaily ?? 0,
        bonusSystemEnabled: !!settings.bonusSystemEnabled,
        dailyActiveUserBonusAmount: settings.dailyActiveUserBonusAmount ?? 5,
        dailyActiveUserMinimumGames: settings.dailyActiveUserMinimumGames ?? 3,
        topWinnerBonusAmount: settings.topWinnerBonusAmount ?? 50,
        topWinnerBonusCount: settings.topWinnerBonusCount ?? 1,
        topWinnerBonusPeriod: settings.topWinnerBonusPeriod || 'daily',
        topWinnerBonusMinimumWinnings: settings.topWinnerBonusMinimumWinnings ?? 0,
        promoCarouselEnabled: settings.promoCarouselEnabled !== false,
        midnightLuckyEnabled: settings.midnightLuckyEnabled !== false,
        midnightLuckyWinnerCount: settings.midnightLuckyWinnerCount ?? 5,
        midnightLuckyAmount: settings.midnightLuckyAmount ?? 10,
        midnightLuckyMinGames: settings.midnightLuckyMinGames ?? 1,
        drawMode: settings.drawMode || 'fair',
        flashDepositPromoEnabled: settings.flashDepositPromoEnabled !== false,
        flashDepositMinHoursBetween: settings.flashDepositMinHoursBetween ?? 6,
        flashDepositDefaultDurationMinutes: settings.flashDepositDefaultDurationMinutes ?? 30,
      });
    }
  }, [settings, reset]);

  const onSubmit = async (data: SettingsForm) => {
    setSaving(true);
    try {
      const result = await dispatch(updateSettings(data) as any);
      if (result.payload) {
        toast.success('Settings updated successfully');
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      toast.error('An error occurred while updating settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const result = await dispatch(resetSettings() as any);
      if (result.payload?.success) {
        toast.success(result.payload.message || 'Settings reset successfully');
        setShowResetDialog(false);
        // Refetch settings after reset
        dispatch(fetchSettings() as any);
      } else {
        toast.error('Failed to reset settings');
      }
    } catch (error) {
      toast.error('An error occurred while resetting settings');
    } finally {
      setResetting(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography variant="h4" sx={{ 
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
            fontWeight: 600,
            mb: 1
          }}>
            Settings
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage application settings and configuration
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexDirection: { xs: 'row', sm: 'row' }
        }}>
          <Chip
            icon={<Settings />}
            label={settings?.isActive ? "System Active" : "System Inactive"}
            color={settings?.isActive ? "success" : "error"}
            variant="outlined"
            sx={{ fontSize: '0.875rem' }}
          />
          <Avatar sx={{ 
            bgcolor: 'primary.main', 
            width: { xs: 48, sm: 56 }, 
            height: { xs: 48, sm: 56 } 
          }}>
            <Settings sx={{ fontSize: { xs: 24, sm: 28 } }} />
          </Avatar>
        </Box>
      </Box>

      {/* Settings Form */}
      <Paper sx={{ 
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            {/* Commission Settings */}
            <Card sx={{ 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 },
                bgcolor: 'primary.50',
                cursor: 'pointer'
              }} onClick={() => toggleSection('commission')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    <Percent />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Commission Settings
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Configure commission and fee percentages
                    </Typography>
                  </Box>
                </Box>
                <IconButton>
                  {expandedSections.commission ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedSections.commission}>
                <CardContent sx={{ pt: 0 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' }, 
                    gap: { xs: 2, sm: 3 } 
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="inviterCommissionPercent"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Inviter Commission (%)"
                            type="number"
                            placeholder="5"
                            error={!!errors.inviterCommissionPercent}
                            helperText={errors.inviterCommissionPercent?.message || 'Percentage commission for successful referrals'}
                            InputProps={{
                              startAdornment: '% ',
                            }}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="minimumCommissionAmount"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Minimum Commission Amount"
                            type="number"
                            placeholder="1"
                            error={!!errors.minimumCommissionAmount}
                            helperText={errors.minimumCommissionAmount?.message || 'Minimum amount for commission payout'}
                            InputProps={{
                              startAdornment: 'ETB ',
                            }}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="bonusRegistrationAmount"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Bonus Registration Amount"
                            type="number"
                            placeholder="0"
                            error={!!errors.bonusRegistrationAmount}
                            helperText={errors.bonusRegistrationAmount?.message || 'Bonus amount given to new users upon registration'}
                            InputProps={{
                              startAdornment: 'ETB ',
                            }}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Collapse>
            </Card>

            {/* Deposit Bonus Settings */}
            <Card sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 },
                bgcolor: 'warning.50',
                cursor: 'pointer'
              }} onClick={() => toggleSection('depositBonus')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                    <AttachMoney />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Deposit Bonuses
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Bonus for depositor and inviter (plus agent deposit commission)
                    </Typography>
                  </Box>
                </Box>
                <IconButton>
                  {expandedSections.depositBonus ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedSections.depositBonus}>
                <CardContent sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1 }}>Agent deposit commission</Typography>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="agentDepositCommissionPercent"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Agent Deposit Commission (%)"
                            type="number"
                            size="small"
                            helperText="Paid to agent inviter only (agent wallet). Does not stack with inviter cash bonus."
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="agentMinimumClaimAmount"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Agent Min Claim (ETB)"
                            type="number"
                            size="small"
                            helperText="Minimum agent wallet balance to request withdrawal"
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Divider />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Depositor (self) bonus</Typography>
                  <Controller
                    name="depositSelfBonusEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                        label="Enable bonus for the player who deposits"
                      />
                    )}
                  />
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="depositSelfBonusPercent"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Self Bonus (%)"
                            type="number"
                            size="small"
                            helperText="If > 0, overrides fixed amount. Goes to bonus balance."
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="depositSelfBonusAmount"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Self Bonus Fixed (ETB)"
                            type="number"
                            size="small"
                            helperText="Used when percent is 0"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="depositSelfBonusFirstOnly"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                            label="First deposit only"
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Divider />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Inviter bonus</Typography>
                  <Controller
                    name="depositInviterBonusEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                        label="Enable bonus for the inviter of the depositor"
                      />
                    )}
                  />
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="depositInviterBonusPercent"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Inviter Bonus (%)"
                            type="number"
                            size="small"
                            helperText="If > 0, overrides fixed. Non-agent inviters only → bonus balance. Agents use deposit commission % above."
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="depositInviterBonusAmount"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Inviter Bonus Fixed (ETB)"
                            type="number"
                            size="small"
                            helperText="Used when percent is 0"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="depositInviterBonusFirstOnly"
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                            label="First deposit only"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Collapse>
            </Card>

            {/* Ops / platform bonus settings */}
            <Card sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 },
                bgcolor: 'info.50',
                cursor: 'pointer'
              }} onClick={() => toggleSection('opsBonus')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                    <Tune />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      House & Daily Bonuses
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      House phone, spin limit, and cron bonus system
                    </Typography>
                  </Box>
                </Box>
                <IconButton>
                  {expandedSections.opsBonus ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedSections.opsBonus}>
                <CardContent sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="houseAccountPhone"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="House Account Phone"
                            size="small"
                            error={!!errors.houseAccountPhone}
                            helperText={errors.houseAccountPhone?.message || 'Receives house fees / deposit alerts (251…)'}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="spinHouseLimitDaily"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Spin House Limit Daily (ETB)"
                            type="number"
                            size="small"
                            helperText="0 = no daily spin house cap"
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Divider />
                  <Controller
                    name="bonusSystemEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                        label="Enable daily active / top winner bonus system"
                      />
                    )}
                  />
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="dailyActiveUserBonusAmount"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Daily Active Bonus (ETB)" type="number" size="small" />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="dailyActiveUserMinimumGames"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Min Games for Daily Bonus" type="number" size="small" />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="topWinnerBonusAmount"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Top Winner Bonus (ETB)" type="number" size="small" />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="topWinnerBonusCount"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Top Winner Count" type="number" size="small" />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="topWinnerBonusPeriod"
                        control={control}
                        render={({ field }) => (
                          <FormControl fullWidth size="small">
                            <InputLabel>Top Winner Period</InputLabel>
                            <Select {...field} label="Top Winner Period">
                              <MenuItem value="daily">Daily</MenuItem>
                              <MenuItem value="weekly">Weekly</MenuItem>
                              <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="topWinnerBonusMinimumWinnings"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Min Winnings to Qualify" type="number" size="small" />
                        )}
                      />
                    </Box>
                  </Box>

                  <Divider />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Lobby promo & midnight lucky</Typography>
                  <Controller
                    name="promoCarouselEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                        label="Show promo carousel on Bingo / Ludo lobbies"
                      />
                    )}
                  />
                  <Controller
                    name="midnightLuckyEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                        label="Enable midnight lucky draw"
                      />
                    )}
                  />
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="midnightLuckyWinnerCount"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Midnight Winners Count" type="number" size="small" />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="midnightLuckyAmount"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Midnight Bonus (ETB)" type="number" size="small" />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="midnightLuckyMinGames"
                        control={control}
                        render={({ field }) => (
                          <TextField {...field} fullWidth label="Min Games to Enter Draw" type="number" size="small" />
                        )}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      ⚡️ Flash Deposit Bonus (Countdown 50% / 100%)
                    </Typography>
                    {flashStatus?.active ? (
                      <Chip
                        label={`🔥 ACTIVE: +${flashStatus.percent}% (${Math.floor((flashStatus.remainingSeconds || 0) / 60)}m left)`}
                        color="error"
                        size="small"
                        sx={{ fontWeight: 'bold' }}
                      />
                    ) : (
                      <Chip label="⚪️ Inactive" variant="outlined" size="small" />
                    )}
                  </Box>

                  {flashActionMsg && (
                    <Alert severity={flashActionMsg.type} onClose={() => setFlashActionMsg(null)} sx={{ my: 1.5 }}>
                      {flashActionMsg.text}
                    </Alert>
                  )}

                  <Controller
                    name="flashDepositPromoEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                        label="Enable automated random flash deposit bonus announcements with countdown"
                      />
                    )}
                  />

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 1, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="flashDepositMinHoursBetween"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Min Cooldown Between Promos (Hours)"
                            type="number"
                            size="small"
                            helperText="Minimum hours to wait before triggering another promo (default: 6)"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="flashDepositDefaultDurationMinutes"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Countdown Duration (Minutes)"
                            type="number"
                            size="small"
                            helperText="How long the deposit countdown lasts (default: 30)"
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  {/* On-demand Trigger Actions Box */}
                  <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      🚀 Instant Promo Controls (Broadcast directly to all Telegram players):
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        disabled={flashActionLoading || !!flashStatus?.active}
                        onClick={() => handleTriggerFlashPromo(100, 30)}
                        startIcon={<Bolt />}
                      >
                        Launch 100% Flash Bonus (30m)
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={flashActionLoading || !!flashStatus?.active}
                        onClick={() => handleTriggerFlashPromo(50, 30)}
                        startIcon={<Bolt />}
                      >
                        Launch 50% Flash Bonus (30m)
                      </Button>
                      {flashStatus?.active && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          disabled={flashActionLoading}
                          onClick={handleCancelFlashPromo}
                        >
                          Cancel Active Promo
                        </Button>
                      )}
                    </Box>
                    {flashStatus?.active && (
                      <Typography variant="caption" color="text.secondary">
                        📊 Current Promo Stats: {flashStatus.totalDepositsCredited || 0} deposits credited, {flashStatus.totalBonusGiven || 0} ETB total bonus given.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Collapse>
            </Card>

            {/* Service Fee Settings */}
            <Card sx={{ 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 },
                bgcolor: 'success.50',
                cursor: 'pointer'
              }} onClick={() => toggleSection('serviceFee')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                    <AttachMoney />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Service Fee Settings
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Configure service fees for different game types
                    </Typography>
                  </Box>
                </Box>
                <IconButton>
                  {expandedSections.serviceFee ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedSections.serviceFee}>
                <CardContent sx={{ pt: 0 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' }, 
                    gap: { xs: 2, sm: 3 } 
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="serviceFeePercent"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="General Service Fee (%)"
                            type="number"
                            placeholder="20"
                            error={!!errors.serviceFeePercent}
                            helperText={errors.serviceFeePercent?.message || 'Service fee for regular games and transactions'}
                            InputProps={{
                              startAdornment: '% ',
                            }}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="tournamentServiceFeePercent"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Tournament Service Fee (%)"
                            type="number"
                            placeholder="20"
                            error={!!errors.tournamentServiceFeePercent}
                            helperText={errors.tournamentServiceFeePercent?.message || 'Service fee specifically for tournaments'}
                            InputProps={{
                              startAdornment: '% ',
                            }}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Collapse>
            </Card>

            {/* Game Settings */}
            <Card sx={{ 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 },
                bgcolor: 'warning.50',
                cursor: 'pointer'
              }} onClick={() => toggleSection('gameSettings')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                    <Casino />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Game Settings
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Configure game timing and player requirements
                    </Typography>
                  </Box>
                </Box>
                <IconButton>
                  {expandedSections.gameSettings ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedSections.gameSettings}>
                <CardContent sx={{ pt: 0 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' }, 
                    gap: { xs: 2, sm: 3 } 
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="drawIntervalSeconds"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Draw Interval (seconds)"
                            type="number"
                            placeholder="30"
                            error={!!errors.drawIntervalSeconds}
                            helperText={errors.drawIntervalSeconds?.message || 'Time interval between number draws (1-300 seconds)'}
                            InputProps={{
                              endAdornment: ' sec',
                            }}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="countdownSeconds"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Countdown (seconds)"
                            type="number"
                            placeholder="10"
                            error={!!errors.countdownSeconds}
                            helperText={errors.countdownSeconds?.message || 'Countdown duration before game starts (5-60 seconds)'}
                            InputProps={{
                              endAdornment: ' sec',
                            }}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="minimumPlayers"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Minimum Players"
                            type="number"
                            placeholder="2"
                            error={!!errors.minimumPlayers}
                            helperText={errors.minimumPlayers?.message || 'Minimum players required to start a game (2-50 players)'}
                            InputProps={{
                              endAdornment: ' players',
                            }}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ mt: 3 }}>
                    <Controller
                      name="drawMode"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth size="small" error={!!errors.drawMode}>
                          <InputLabel id="draw-mode-label">Number draw mode</InputLabel>
                          <Select
                            {...field}
                            labelId="draw-mode-label"
                            label="Number draw mode"
                          >
                            <MenuItem value="fair">1 — Fair (default, same chance for all)</MenuItem>
                            <MenuItem value="bot_advantage">2 — Bot advantage (prefer bot card numbers)</MenuItem>
                            <MenuItem value="block_human">3 — Block human bingo (bots win almost always)</MenuItem>
                          </Select>
                          <FormHelperText>
                            {field.value === 'fair' && 'Random draws. Real players and bots have the same chance.'}
                            {field.value === 'bot_advantage' && 'Draws prefer numbers on bot cards. Humans can still win.'}
                            {field.value === 'block_human' && 'Avoids numbers that would complete a human bingo. Use only when bots are in the room.'}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Box>
                </CardContent>
              </Collapse>
            </Card>

            {/* System Status */}
            <Card sx={{ 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 },
                bgcolor: 'info.50',
                cursor: 'pointer'
              }} onClick={() => toggleSection('systemStatus')}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                    <Security />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      System Status
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Control system-wide settings and features
                    </Typography>
                  </Box>
                </Box>
                <IconButton>
                  {expandedSections.systemStatus ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={expandedSections.systemStatus}>
                <CardContent sx={{ pt: 0 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 2,
                    bgcolor: systemActive ? 'success.50' : 'error.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: systemActive ? 'success.200' : 'error.200'
                  }}>
                    <Box>
                      <Typography variant="h6" sx={{ 
                        color: systemActive ? 'success.main' : 'error.main',
                        fontWeight: 600
                      }}>
                        {systemActive ? 'System Active' : 'System Inactive'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {systemActive 
                          ? 'All services are running normally' 
                          : 'System is in maintenance mode'
                        }
                      </Typography>
                    </Box>
                    <Controller
                      name="isActive"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color={field.value ? "success" : "error"}
                          size="medium"
                        />
                      )}
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    mb: 2
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="maxBotsPerRoom"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Max Bots Per Room"
                            type="number"
                            error={!!errors.maxBotsPerRoom}
                            helperText={
                              errors.maxBotsPerRoom?.message ||
                              'Traffic still varies by time of day, but never above this. Applies on the next game (no restart).'
                            }
                            InputProps={{ endAdornment: ' bots' }}
                            size="small"
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 2,
                    bgcolor: adminOnly ? 'warning.50' : 'grey.100',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: adminOnly ? 'warning.200' : 'grey.300'
                  }}>
                    <Box>
                      <Typography variant="h6" sx={{ 
                        color: adminOnly ? 'warning.main' : 'text.primary',
                        fontWeight: 600
                      }}>
                        Bots Enabled
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {adminOnly
                          ? 'Bots will join waiting rooms (next game). No server restart needed.'
                          : 'Bots will not join new games. Games already in progress are left alone.'}
                      </Typography>
                    </Box>
                    <Controller
                      name="botEnabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          color="warning"
                          size="medium"
                          inputProps={{ 'aria-label': 'bots enabled toggle' }}
                        />
                      )}
                    />
                  </Box>
                </CardContent>
              </Collapse>
            </Card>
          </Box>

          <Divider sx={{ my: { xs: 2, sm: 3 } }} />

          {/* Last Updated Info */}
          {settings?.updatedAt && (
            <Box sx={{ 
              mb: { xs: 2, sm: 3 },
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                justifyContent: { xs: 'center', sm: 'flex-start' }
              }}>
                <Tune fontSize="small" />
                Last updated: {dayjs(settings.updatedAt).format('MMM DD, YYYY HH:mm:ss')}
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            gap: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Refresh />}
              onClick={() => setShowResetDialog(true)}
              disabled={loading || saving}
              size="large"
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: '140px' }
              }}
            >
              Reset to Defaults
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || saving || !isDirty}
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              size="large"
              sx={{ 
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: '140px' },
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Reset Confirmation Dialog */}
      <Dialog 
        open={showResetDialog} 
        onClose={() => setShowResetDialog(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 1, sm: 2 },
            maxHeight: { xs: 'calc(100dvh - 16px)', sm: '80vh' },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ 
          padding: { xs: 2, sm: 3 },
          paddingBottom: { xs: 1, sm: 2 }
        }}>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Refresh />
            Reset Settings to Defaults
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0, 
          padding: { xs: 2, sm: 3 },
          '&.MuiDialogContent-root': {
            paddingTop: { xs: 1, sm: 2 },
          }
        }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to reset all settings to their default values? This action cannot be undone.
          </Alert>
          <Typography variant="body2" color="textSecondary">
            This will reset all commission percentages, service fees, and other settings to their original values.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: { xs: 'column', sm: 'row' }, 
          gap: { xs: 1, sm: 0 },
          padding: { xs: 2, sm: 1 },
          '& > *': {
            width: { xs: '100%', sm: 'auto' },
          }
        }}>
          <Button 
            onClick={() => setShowResetDialog(false)} 
            disabled={resetting}
            variant="outlined"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReset}
            color="warning"
            variant="contained"
            disabled={resetting}
            startIcon={resetting ? <CircularProgress size={20} /> : <Refresh />}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {resetting ? 'Resetting...' : 'Reset Settings'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage; 